import * as React from 'react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    Avatar,
    Button,
    Card,
    CardActions,
    CircularProgress,
    Box,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useLogin, useNotify } from 'react-admin';

const Login = () => {
    const [loading, setLoading] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const user = localStorage.getItem('user');
    const notify = useNotify();
    const login = useLogin();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (user) {
            navigate('/');
        }
    }, [user, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        login(
            { phone: username, password },
            location.state ? (location.state as any).nextPathname : '/',
        ).catch((error: any) => {
            setLoading(false);
            notify(
                typeof error === 'string'
                    ? error
                    : typeof error === 'undefined' || !error.message
                    ? 'ra.auth.sign_in_error'
                    : error.message,
                { type: 'error' },
            );
        });
    };

    return (
        <Box
            component="main"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: theme => theme.palette.grey[100],
                padding: 2,
            }}
        >
            <Card sx={{ width: { xs: '95%', sm: 520 }, p: 4, boxShadow: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64 }}>
                        <LockIcon sx={{ fontSize: 36 }} />
                    </Avatar>
                    <Typography variant="h4" sx={{ mt: 2, fontWeight: 700 }}>
                        Welcome back!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Log in to your Kaksya account
                    </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }} noValidate>
                    <TextField
                        label="Username"
                        required
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        disabled={loading}
                        fullWidth
                        margin="normal"
                    />

                    <TextField
                        label="Password"
                        required
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        disabled={loading}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(s => !s)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <CardActions sx={{ padding: 0, mt: 5 }}>
                        <Button
                            variant="contained"
                            type="submit"
                            disabled={loading || !username.trim() || !password}
                            fullWidth
                            sx={{ py: 1.5 }}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'Login'}
                        </Button>
                    </CardActions>
                </Box>
            </Card>
        </Box>
    );
};

Login.propTypes = {
    authProvider: PropTypes.func,
    previousRoute: PropTypes.string,
};

export default Login;
